/**
 * @fileoverview Feature flag utilities for gradual rollout of features
 */

import { CHAT_AGENT_SHORT_NAME } from "@/constants/agents";
import { AgentInstance } from "@/types/agent";

/**
 * Check if a tenant is enabled for the global chat feature
 * Reads from VITE_GLOBAL_CHAT_ENABLED_TENANTS environment variable
 * @param tenantId - The tenant ID to check
 * @returns boolean - True if tenant is enabled for global chat
 */
export function isGlobalChatEnabledForTenant(tenantId: string | undefined): boolean {
  if (!tenantId) return false;

  const enabledTenants = import.meta.env.VITE_GLOBAL_CHAT_ENABLED_TENANTS;
  
  if (!enabledTenants) return false;

  // Split by comma and trim whitespace, then check if tenant ID exists
  const tenantList = enabledTenants
    .split(',')
    .map((id: string) => id.trim())
    .filter((id: string) => id.length > 0);

  return tenantList.includes(tenantId);
}

/**
 * Resolve the first workflow instance ID for an agent identified by its short name.
 * Returns "" when the agent (or its workflow instance) is not yet provisioned for
 * the tenant — callers should treat empty as "not ready".
 */
export function getWorkflowInstanceIdByAgentShortName(
  agentInstances: AgentInstance[] | undefined,
  shortName: string,
): string {
  const instance = agentInstances?.find(
    (i) => i?.agent?.short_name === shortName,
  );
  return instance?.workflow_instances?.[0]?.id ?? "";
}

/**
 * Get the chat workflow instance ID for the global chat agent.
 */
export function getChatWorkflowInstanceId(agentInstances: AgentInstance[] | undefined ): string {
  return getWorkflowInstanceIdByAgentShortName(agentInstances, CHAT_AGENT_SHORT_NAME);
}
